'use client';

import {useId, useRef, useState} from 'react';

import classNames from 'classnames';

import {useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {Button} from '@udemy-v2/react-core-components';

import {
    trackCommunityCardPresented,
    trackCommunityCardSelected,
    trackCommunityListExpanded,
} from '@/features/communities/lib/tracking';
import type {CommunityListing, CommunityListingSet} from '@/features/communities/lib/types';

import {CategoryFilters} from './category-filters.react-component';
import {CommunityCard} from './community-card.react-component';
import {IS_REVEAL_ENABLED} from './reveal-config';

import styles from './community-showcase.module.scss';

/**
 * Cards visible before "Show more" (when `IS_REVEAL_ENABLED`), per breakpoint — the 5×3 grid Figma draws on desktop
 * and the 6 stacked cards it draws on mobile.
 *
 * The difference is honoured in CSS rather than with a breakpoint hook: the route is
 * statically rendered, so measuring the viewport in JS would paint the desktop count first
 * and narrow it after hydration. The grid always renders up to the desktop count and hides
 * the surplus below the mobile breakpoint.
 */
const INITIAL_CARD_COUNT = 15;
const INITIAL_CARD_COUNT_MOBILE = 6;

export interface CommunityShowcaseProps {
    /**
     * Every published community, fetched unfiltered on the server. The chips filter
     * this set in memory — there is no round trip per selection — so the whole set
     * ships in the RSC payload by design (decision 2.1).
     */
    listingSet: CommunityListingSet;
    /**
     * Chip labels from the `filterable_categories` feature variable, in flag order.
     * Empty means no filter UI at all: the chips row is not rendered and the grid
     * shows everything.
     */
    availableCategories: string[];
}

/**
 * Community discover section — Figma node 5260:156740 (desktop) and 5260:160518 (mobile),
 * file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A topic filter, the instructor card grid, and a "Show more" control. Sub-nodes:
 * filters 5260:156744 / 5260:160521, grid
 * 5260:156757 / 5260:160535, "Show more" 5260:156773 / 5260:160542.
 *
 * This is the page's only stateful section, and the state is entirely local: the server
 * hands down every published community, and both the filter and the reveal work on that
 * set in memory. Nothing here fetches or navigates, which is what lets the route stay
 * `force-static` (decision 2.1).
 */
export function CommunityShowcase({listingSet, availableCategories}: CommunityShowcaseProps) {
    const {gettext} = useI18n();
    const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set<string>());
    const [isExpanded, setIsExpanded] = useState(false);
    const revealButtonRef = useRef<HTMLElement>(null);
    const gridId = useId();

    /**
     * Communities already reported as presented, so each one is counted once per page
     * instance rather than once per mount.
     *
     * `TrackImpression`'s `onlyOnce` lives in the observer it unmounts with, and both
     * controls here unmount cards: collapsing the list drops everything past the initial
     * count, and a filter change drops everything outside the selection. Re-revealing any
     * of them mounts a fresh observer, which would report the same card again — and in
     * development React's StrictMode double-invokes the effect, doubling every impression
     * on top of that. The schema asks for one impression per card per page instance, so
     * the "seen" set has to outlive the cards.
     */
    const presentedCommunityIds = useRef(new Set<string>());

    const {listings} = listingSet;

    // An empty selection means "All", so there is no separate all-categories branch. The
    // match is a union on exact display names — the same semantics as the gateway's own
    // `categories` argument, so this section filters exactly as that query would.
    const filtered =
        selected.size === 0
            ? listings
            : listings.filter((listing) => listing.categories.some((c) => selected.has(c)));

    // With the reveal control off there is nothing to expand, so everything is shown.
    const showAll = isExpanded || !IS_REVEAL_ENABLED;
    const visible = showAll ? filtered : filtered.slice(0, INITIAL_CARD_COUNT);

    // One click reveals everything remaining in the current filter; a second click puts it
    // back to the initial count. The whole set is already in the browser, so neither
    // direction fetches anything.
    //
    // The condition is the same either way — the control is worth showing exactly when the
    // two states differ, i.e. when the filtered set is longer than what the breakpoint shows
    // collapsed. It therefore does *not* depend on `isExpanded`; only the label and glyph do.
    // The two breakpoints reveal at different counts, so the control can be meaningful on
    // mobile while desktop already shows everything.
    const isTogglableOnMobile = filtered.length > INITIAL_CARD_COUNT_MOBILE;
    const isTogglableOnDesktop = filtered.length > INITIAL_CARD_COUNT;

    // Expansion is deliberately *not* reset here (Ali, 2026-08-14). The two controls are
    // independent: once the visitor has asked to see everything, every later filter shows
    // its whole result set, however long, and the reveal control only comes back if it is
    // still hiding something. The reverse — collapsing on each selection change, which is
    // what this component did first and what the plan prescribed — meant someone who had
    // opted into the full list was silently put back into a truncated one on every chip
    // click.
    function selectCategory(category: string) {
        setSelected((previous) => {
            const next = new Set(previous);

            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }

            return next;
        });
    }

    // Deliberately not `useCallback`: a new identity each render is fine, because
    // `TrackImpression` reads `trackFunc` through an effect keyed on `inView`, not on the
    // function, and the dedupe lives in the ref rather than in the closure.
    function presentCard(listing: CommunityListing) {
        if (presentedCommunityIds.current.has(listing.communityId)) {
            return;
        }

        presentedCommunityIds.current.add(listing.communityId);
        trackCommunityCardPresented(listing);
    }

    function selectAllCategories() {
        setSelected(new Set<string>());
    }

    function toggleExpanded() {
        const isCollapsing = isExpanded;

        setIsExpanded(!isExpanded);

        if (!isCollapsing) {
            // Only the expanding direction is tracked: the schema has no collapse event,
            // and `itemCountAfterExpansion` is the whole filtered set because that is what
            // expanding reveals — the collapsed counts differ per breakpoint in CSS alone,
            // so there is no single "visible count" to report from here.
            trackCommunityListExpanded(filtered.length);
        }

        if (isCollapsing) {
            // Collapsing removes up to 35 cards' worth of page — several viewports of it —
            // and the browser keeps the scroll offset, clamping it to the shorter document.
            // Someone who scrolled down to reach the button would be dropped into whatever
            // now sits at that offset (the testimonials, or the page's end). Pulling the
            // button back into view keeps them where they acted. `block: 'nearest'` moves
            // the minimum needed, so nothing happens when the button is already on screen.
            // Optional call, as `InstructorTestimonials` does: jsdom does not implement
            // `scrollIntoView`, and this is a nicety rather than something worth throwing on.
            requestAnimationFrame(() => {
                revealButtonRef.current?.scrollIntoView?.({block: 'nearest'});
            });
        }
    }

    return (
        <section className={styles['showcase']} data-purpose="community-showcase">
            <div className={styles['inner']}>
                {/* No chips at all when the flag configures no categories — that is a
                    supported configuration, not a failure, and it means "no filter UI". */}
                {availableCategories.length > 0 && (
                    <CategoryFilters
                        categories={availableCategories}
                        selected={selected}
                        onToggle={selectCategory}
                        onSelectAll={selectAllCategories}
                    />
                )}
                {visible.length > 0 ? (
                    <ul className={styles['grid']} id={gridId}>
                        {visible.map((listing, index) => (
                            <CommunityCard
                                key={`${listing.instructor.id}-${listing.name}`}
                                listing={listing}
                                isHiddenOnMobile={!showAll && index >= INITIAL_CARD_COUNT_MOBILE}
                                onPresented={() => presentCard(listing)}
                                onSelected={() => trackCommunityCardSelected(listing)}
                            />
                        ))}
                    </ul>
                ) : (
                    // Neither empty state is drawn in Figma (data plan open item 8), and
                    // they are distinct: one is the filter's answer, the other is the whole
                    // section having nothing to show — reachable when every listing was
                    // dropped as unrenderable, or when the fetch failed and returned an
                    // empty set. Both strings want a copy decision.
                    <p className={styles['empty']} data-purpose="community-showcase-empty">
                        {selected.size > 0
                            ? gettext('No communities match the topics you selected.')
                            : gettext('There are no communities to show right now.')}
                    </p>
                )}
                {IS_REVEAL_ENABLED && isTogglableOnMobile && (
                    <div
                        className={classNames(styles['reveal'], {
                            [styles['reveal-mobile-only']]: !isTogglableOnDesktop,
                        })}
                    >
                        <Button
                            ref={revealButtonRef}
                            udStyle="secondary"
                            size="small"
                            onClick={toggleExpanded}
                            aria-expanded={isExpanded}
                            aria-controls={gridId}
                            data-purpose="community-showcase-reveal-toggle"
                        >
                            {isExpanded ? gettext('Show Less') : gettext('Show More')}
                            <Icon glyph={isExpanded ? 'collapse' : 'expand'} label={false} />
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}
